/* Collection tabs slider  <collection-tabs>
   - inactive tabs live in <template>; cloned on first open (or on hover intent)
   - native scroll-snap track, arrows page by visible width
   - page counter + progress bar, animated tab ink, keyboard tabs */
(() => {
  if (customElements.get('collection-tabs')) return;
  const pad = n => String(n).padStart(2, '0');

  class CollectionTabs extends HTMLElement {
    connectedCallback() {
      this.tabs = [...this.querySelectorAll('[data-ct-tab]')];
      this.panels = [...this.querySelectorAll('[data-ct-panel]')];
      this.prev = this.querySelectorAll('[data-ct-prev]');
      this.next = this.querySelectorAll('[data-ct-next]');
      this.ink = this.querySelector('.ct__ink');
      this.viewAll = this.querySelector('[data-ct-viewall]');
      this.index = 0;
      this._onScroll = this._raf(() => this._sync());

      this.tabs.forEach((t, i) => {
        t.addEventListener('click', () => this.select(i));
        // Warm the next tab on hover intent so it is instant on click
        t.addEventListener('pointerenter', () => this._materialize(this.panels[i]), { once: true });
        t.addEventListener('keydown', e => this._key(e, i));
      });
      this.prev.forEach(b => b.addEventListener('click', () => this._page(-1)));
      this.next.forEach(b => b.addEventListener('click', () => this._page(1)));
      this._attach(this.panels[0]);
      this._prefetch();
      this._moveInk(false);
      this._ro = new ResizeObserver(this._raf(() => { this._moveInk(false); this._sync(); }));
      this._ro.observe(this);
      if (window.Shopify && Shopify.designMode) {
        this.addEventListener('shopify:block:select', e => {
          const i = this.tabs.findIndex(t => t.id === 'CTab-' + e.detail.blockId);
          if (i > -1) this.select(i);
        });
      }
    }
    disconnectedCallback() { this._ro?.disconnect(); }

    _raf(fn) { let q = false; return () => { if (q) return; q = true; requestAnimationFrame(() => { q = false; fn(); }); }; }

    _materialize(panel) {
      if (!panel) return Promise.resolve();
      const tpl = panel.querySelector('template[data-ct-template]');
      if (tpl) { panel.appendChild(tpl.content.cloneNode(true)); tpl.remove(); }
      return panel.dataset.ctRemote ? this._load(panel) : Promise.resolve();
    }

    /* Remote tabs: products come from the ct-panel section only when needed */
    _load(panel) {
      if (panel._ctLoad) return panel._ctLoad;
      panel.setAttribute('aria-busy', 'true');
      panel._ctLoad = fetch(panel.dataset.ctRemote, { credentials: 'same-origin' })
        .then(r => { if (!r.ok) throw new Error(r.status); return r.text(); })
        .then(html => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const track = doc.querySelector('[data-ct-track]');
          if (!track) throw new Error('no track');
          const limit = parseInt(panel.dataset.ctLimit, 10) || 12;
          [...track.querySelectorAll('.ct__item')].slice(limit).forEach(n => n.remove());
          const more = panel.querySelector('template[data-ct-more]');
          if (more) { track.appendChild(more.content.cloneNode(true)); more.remove(); }
          panel.appendChild(document.importNode(track, true));
          window._wishlist?._updateAllBtns?.();
          document.dispatchEvent(new CustomEvent('tt:cards:added', { detail: { root: panel } }));
        })
        .catch(() => {
          panel._ctLoad = null;
          if (!panel.querySelector('[data-ct-track]')) {
            const a = document.createElement('a');
            a.className = 'ct__fallback'; a.href = panel.dataset.viewUrl || '/collections/all';
            a.textContent = 'View these products';
            panel.replaceChildren(a);
          }
        })
        .finally(() => panel.removeAttribute('aria-busy'));
      return panel._ctLoad;
    }

    /* Fetch the other tabs quietly once the section is close to the screen */
    _prefetch() {
      const remote = this.panels.filter(p => p.dataset.ctRemote);
      if (!remote.length || !('IntersectionObserver' in window)) return;
      const io = new IntersectionObserver(entries => {
        if (!entries.some(e => e.isIntersecting)) return;
        io.disconnect();
        const idle = window.requestIdleCallback || (fn => setTimeout(fn, 400));
        idle(() => remote.reduce((p, panel) => p.then(() => this._load(panel)), Promise.resolve()));
      }, { rootMargin: '400px 0px' });
      io.observe(this);
    }

    _attach(panel) {
      this.track?.removeEventListener('scroll', this._onScroll);
      this.track = panel?.querySelector('[data-ct-track]');
      if (!this.track) return;
      this.track.addEventListener('scroll', this._onScroll, { passive: true });
      this._drag(this.track);
      this._sync();
    }

    select(i, focus = false) {
      if (i === this.index || !this.panels[i]) return;
      const from = this.panels[this.index];
      const to = this.panels[i];
      const ready = this._materialize(to);
      this.tabs.forEach((t, n) => {
        const on = n === i;
        t.classList.toggle('is-active', on);
        t.setAttribute('aria-selected', String(on));
        t.tabIndex = on ? 0 : -1;
      });
      if (focus) this.tabs[i].focus();
      this.tabs[i].scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
      this.index = i;
      this._moveInk(true);

      const swap = () => {
        from.hidden = true; from.classList.remove('is-active', 'is-leaving');
        to.hidden = false;
        const t = to.querySelector('[data-ct-track]');
        if (t) t.scrollLeft = 0;
        to.classList.remove('is-entering'); void to.offsetWidth;
        to.classList.add('is-active', 'is-entering');
        this._attach(to);
        if (this.viewAll && to.dataset.viewUrl) this.viewAll.href = to.dataset.viewUrl;
      };
      if (to.dataset.ctRemote && !to.querySelector('[data-ct-track]')) {
        // Not loaded yet: show the panel with a short loading state, then fill it
        this.tabs[i].setAttribute('aria-busy', 'true');
        to.classList.add('is-loading');
        ready.then(() => {
          this.tabs[i].removeAttribute('aria-busy');
          to.classList.remove('is-loading');
          if (this.index === i) this._attach(to);
        });
      }
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reduce || this.closest('.ct--anim-none')) { swap(); return; }
      // Lock the stage height during the swap so the page below does not jump
      const stage = this.querySelector('.ct__stage');
      stage.style.minHeight = stage.offsetHeight + 'px';
      from.classList.add('is-leaving');
      setTimeout(() => { swap(); setTimeout(() => { stage.style.minHeight = ''; }, 450); }, 160);
    }

    _key(e, i) {
      const n = this.tabs.length;
      let to = null;
      if (e.key === 'ArrowRight') to = (i + 1) % n;
      if (e.key === 'ArrowLeft') to = (i - 1 + n) % n;
      if (e.key === 'Home') to = 0;
      if (e.key === 'End') to = n - 1;
      if (to !== null) { e.preventDefault(); this.select(to, true); }
    }

    _moveInk(animate) {
      const t = this.tabs[this.index];
      if (!this.ink || !t) return;
      this.ink.classList.toggle('is-static', !animate);
      this.ink.style.width = t.offsetWidth + 'px';
      this.ink.style.height = t.offsetHeight + 'px';
      this.ink.style.transform = `translate(${t.offsetLeft}px, ${t.offsetTop}px)`;
    }

    _step() {
      const item = this.track?.querySelector('.ct__item');
      if (!item) return this.track?.clientWidth || 0;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      const w = item.getBoundingClientRect().width + gap;
      return Math.max(w, Math.floor((this.track.clientWidth + gap) / w) * w);
    }
    _page(dir) {
      if (!this.track) return;
      this.track.scrollBy({ left: dir * this._step(), behavior: 'smooth' });
    }
    _sync() {
      const t = this.track;
      if (!t) return;
      const max = t.scrollWidth - t.clientWidth;
      const atStart = t.scrollLeft <= 2, atEnd = t.scrollLeft >= max - 2;
      this.prev.forEach(b => { b.disabled = atStart; });
      this.next.forEach(b => { b.disabled = atEnd || max <= 2; });
      this.classList.toggle('is-static', max <= 2);
      const step = this._step() || 1;
      const pages = Math.max(1, Math.ceil((t.scrollWidth - 2) / step) - Math.floor(t.clientWidth / step) + 1);
      const page = atEnd ? pages : Math.min(pages, Math.round(t.scrollLeft / step) + 1);
      const p = this.querySelector('[data-ct-page]'), ps = this.querySelector('[data-ct-pages]'), bar = this.querySelector('[data-ct-bar]');
      if (p) p.textContent = pad(page);
      if (ps) ps.textContent = pad(pages);
      if (bar) bar.style.transform = `scaleX(${max > 0 ? Math.max(t.scrollLeft / max, 1 / pages) : 1})`;
    }

    /* Mouse drag (touch scrolls natively) */
    _drag(track) {
      if (track._ctDrag) return;
      track._ctDrag = true;
      let down = false, moved = false, x0 = 0, s0 = 0;
      track.addEventListener('pointerdown', e => {
        if (e.pointerType !== 'mouse' || e.button !== 0 || e.target.closest('button')) return;
        down = true; moved = false; x0 = e.clientX; s0 = track.scrollLeft;
      });
      window.addEventListener('pointermove', e => {
        if (!down) return;
        const dx = e.clientX - x0;
        if (!moved && Math.abs(dx) > 6) { moved = true; track.classList.add('is-dragging'); }
        if (moved) track.scrollLeft = s0 - dx;
      });
      window.addEventListener('pointerup', () => {
        if (!down) return;
        down = false;
        track.classList.remove('is-dragging');
        if (moved) {
          const stop = ev => { ev.preventDefault(); ev.stopPropagation(); };
          track.addEventListener('click', stop, { capture: true, once: true });
          setTimeout(() => track.removeEventListener('click', stop, true), 60);
        }
      });
      track.addEventListener('dragstart', e => e.preventDefault());
    }
  }
  customElements.define('collection-tabs', CollectionTabs);
})();
